import React from "react"
import PropTypes from "prop-types"
import Helmet from "react-helmet"
import { ToastProvider } from "gatsby-interface"
import GlobalStyles from "../stylesheets/GlobalStyles"
import { isAuthenticated } from "@modules/auth"
import { SkipNavTrigger, SkipNavTarget } from "@modules/a11y"
import { GenericErrorProvider } from "@modules/modal"
import { Header } from "@modules/header/components/Header"
import { AuthorizedFeatureFlagProvider } from "@modules/featureFlags"
import { MarketingBanner } from "@modules/banner"
import Providers from "@modules/ui/layouts/Providers"
import { SourceControlProvider } from "@modules/graphql/types"
import { useAvailableSourceProvidersQuery } from "@modules/site/create/Import/queries.generated"
import { useCurrentUserQuery } from "@modules/auth/queries.generated"
import { useTracker, SegmentEventType } from "@modules/analytics"
import NetPromoterSurvey from "@modules/netPromoterSurvey"

function testPathnameForBg(path) {
  return (
    /\/dashboard\/sites\/*$/.test(path) ||
    /\/get-started\/*$/.test(path) ||
    /\/details\/*$/.test(path) ||
    /\/dashboard\/pricing\//.test(path)
  )
}

let bodyRef
let lastPathname
let greyBg = false

export function MainLayout({ children, pathname, dashboard = true }) {
  if (!bodyRef) {
    bodyRef = document.querySelector(`body`)
  }

  if (!lastPathname || pathname !== lastPathname) {
    greyBg = testPathnameForBg(pathname)

    if (greyBg) {
      bodyRef.classList.add(`dashboardGreyBg`)
    } else {
      bodyRef.classList.remove(`dashboardGreyBg`)
    }
  }

  lastPathname = pathname

  const shouldShowHeader =
    !pathname.includes(`/previews/login`) &&
    !pathname.includes(`maintenance`) &&
    !pathname.includes(`/dashboard/signup`) &&
    !pathname.includes(`/dashboard/welcome`) &&
    !pathname.includes(`/dashboard/login`) &&
    !pathname.includes(`/dashboard/sso`) &&
    (!pathname.includes(`/site-create`) ||
      (pathname.includes(`/site-create`) && isAuthenticated()))

  const content = (
    <React.Fragment>
      {dashboard && <SkipNavTarget createAnchor />}
      {children}
    </React.Fragment>
  )

  return (
    <Providers>
      <SkipNavTrigger />
      <GlobalStyles />
      <Helmet
        defaultTitle="Gatsby Cloud"
        titleTemplate="Gatsby Cloud | %s"
        htmlAttributes={{
          lang: "en",
        }}
      />
      <IdentifyUser />
      <ToastProvider>
        <GenericErrorProvider>
          {isAuthenticated() && dashboard ? (
            <AuthorizedFeatureFlagProvider>
              {process.env.GATSBY_MAINTENANCE !== `true` && shouldShowHeader && (
                <React.Fragment>
                  <Header pathname={pathname} />
                </React.Fragment>
              )}
              {content}
            </AuthorizedFeatureFlagProvider>
          ) : process.env.GATSBY_MAINTENANCE !== `true` && shouldShowHeader ? (
            <React.Fragment>
              <Header pathname={pathname} />
              {content}
            </React.Fragment>
          ) : (
            content
          )}
        </GenericErrorProvider>
      </ToastProvider>
    </Providers>
  )
}

MainLayout.propTypes = {
  pathname: PropTypes.string,
}

/**
 * HELPERS
 */

function IdentifyUser() {
  const { data } = useCurrentUserQuery({
    fetchPolicy: `cache-only`,
  })
  const currentUser = data?.currentUser

  const { data: sourceProvidersData } = useAvailableSourceProvidersQuery()
  const providers = sourceProvidersData?.availableSourceProviders

  const { trackSegment } = useTracker()

  React.useEffect(() => {
    const canTrack = currentUser?.name && currentUser?.email && providers

    if (!canTrack) {
      return
    }

    const connectedProviders = []

    for (const provider of providers) {
      if (provider.connected && provider.source) {
        connectedProviders.push(provider.source)
      }
    }

    trackSegment({
      type: SegmentEventType.Identify,
      traits: {
        name: currentUser.name,
        email: currentUser.email,
        integrations: {
          GitHub: connectedProviders.includes(SourceControlProvider.Github),
          GitLab: connectedProviders.includes(SourceControlProvider.Gitlab),
          Bitbucket: connectedProviders.includes(
            SourceControlProvider.Bitbucket
          ),
        },
      },
    })
  }, [providers, currentUser?.name, currentUser?.email])

  return null
}
